import React, { useState } from 'react';
import { 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Star, 
  StarOff, 
  Search, 
  X,
  BookOpen,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Quote } from '@brovicms/models';
import { quoteService } from '../../services/quoteService';
import EditQuoteModal from './EditQuoteModal';
import toast from 'react-hot-toast';

interface QuoteTableProps {
  quotes: Quote[];
  onRefresh: () => void;
  loading?: boolean;
}

const QuoteTable: React.FC<QuoteTableProps> = ({ 
  quotes, 
  onRefresh, 
  loading = false 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'author'>('newest');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);


  const handleDelete = async (quote: Quote) => {
    if (window.confirm(`Are you sure you want to delete this quote?`)) {
      try {
        const result = await quoteService.deleteQuote(quote.id);
        if (result.success) {
          onRefresh();
        }
      } catch (error: any) {
        console.error('Delete error:', error);
        toast.error('Failed to delete quote');
      }
    }
  };

  const handleTogglePublished = async (quote: Quote) => {
    try {
      if (quote.isPublished) {
        await quoteService.unpublishQuote(quote.id);
      } else {
        await quoteService.publishQuote(quote.id);
      }
      onRefresh();
    } catch (error: any) {
      console.error('Toggle published error:', error);
      toast.error('Failed to update quote status');
    }
  };

  const handleToggleFeatured = async (quote: Quote) => {
    try {
      await quoteService.toggleFeatured(quote.id, !quote.isFeatured);
      onRefresh();
    } catch (error: any) {
      console.error('Toggle featured error:', error);
      toast.error('Failed to update featured status');
    }
  };

  const handleEdit = (quote: Quote) => {
    setSelectedQuote(quote);
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    onRefresh();
    setShowEditModal(false);
    setSelectedQuote(null);
  };

  const handleEditClose = () => {
    setShowEditModal(false);
    setSelectedQuote(null);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const getStatusBadge = (quote: Quote) => {
    if (quote.isFeatured) {
      return <span className="badge badge-warning">Featured</span>;
    }
    if (quote.isPublished) {
      return <span className="badge badge-success">Published</span>;
    }
    return <span className="badge badge-secondary">Draft</span>;
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = quote.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (quote.source && quote.source.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  const sortedQuotes = [...filteredQuotes].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'oldest':
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      case 'author':
        return (a.source || '').localeCompare(b.source || '');
      default:
        return 0;
    }
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedQuotes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedQuotes = sortedQuotes.slice(startIndex, endIndex);

  // Reset to first page when search term changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-gray-600">Loading quotes...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Enhanced Search Bar */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            {/* Enhanced Search */}
            <div className="flex-1 w-full">
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                </div>
                <input
                  type="text"
                  placeholder="Search quotes by content or author..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md focus:shadow-lg"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Sort Options */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="author">Author A-Z</option>
              </select>
            </div>
          </div>

          {/* Search Results Info */}
          {searchTerm && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Found <span className="font-semibold text-primary-600">{sortedQuotes.length}</span> quote{sortedQuotes.length !== 1 ? 's' : ''} 
                  {searchTerm && ` matching "${searchTerm}"`}
                </p>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                  >
                    Clear search
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Pagination Controls */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Items per page selector */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Show:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  className="px-3 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>

              {/* Pagination info */}
              <div className="text-sm text-gray-600">
                Showing {startIndex + 1} to {Math.min(endIndex, sortedQuotes.length)} of {sortedQuotes.length} quotes
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Quotes Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Quote</th>
                  <th>Source</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedQuotes.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12">
                      <div className="text-gray-500">
                        <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg font-medium">No quotes found</p>
                        <p className="text-sm">Try adjusting your search or filters</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedQuotes.map((quote) => (
                    <tr key={quote.id}>
                      <td>
                        <div className="max-w-xs">
                          <div className="font-medium text-gray-900 truncate">
                            Quote
                          </div>
                          <div className="text-sm text-gray-500 truncate mt-1 italic">
                            "{quote.content.substring(0, 80)}
                            {quote.content.length > 80 ? '...' : ''}"
                          </div>
                          {quote.bookReference && (
                            <div className="flex items-center mt-1 text-xs text-primary-600">
                              <BookOpen className="w-3 h-3 mr-1" />
                              {quote.bookReference.bookTitle}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="text-sm text-gray-900">
                          {quote.source || 'Unknown'}
                        </div>
                      </td>
                      <td>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {quote.category.name}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(quote)}
                          {quote.isPremium && (
                            <span className="badge badge-warning">Premium</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="text-sm text-gray-900">
                          {formatDate(quote.createdAt)}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          {/* <button
                            onClick={() => handleTogglePublished(quote)}
                            className={`p-1 rounded-apple ${
                              quote.isPublished 
                                ? 'text-green-600 hover:text-green-700' 
                                : 'text-gray-400 hover:text-green-600'
                            }`}
                            title={quote.isPublished ? 'Unpublish' : 'Publish'}
                          >
                            {quote.isPublished ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button> */}

                          {/* <button
                            onClick={() => handleToggleFeatured(quote)}
                            className={`p-1 rounded-apple ${
                              quote.isFeatured 
                                ? 'text-yellow-600 hover:text-yellow-700' 
                                : 'text-gray-400 hover:text-yellow-600'
                            }`}
                            title={quote.isFeatured ? 'Remove from featured' : 'Add to featured'}
                          >
                            {quote.isFeatured ? <Star className="w-4 h-4" /> : <StarOff className="w-4 h-4" />}
                          </button> */}

                          <button
                            onClick={() => handleEdit(quote)}
                            className="p-1 text-gray-400 hover:text-primary-600 rounded-apple"
                            title="Edit quote"
                          >
                            <Edit className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => handleDelete(quote)}
                            className="p-1 text-gray-400 hover:text-red-600 rounded-apple"
                            title="Delete quote"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="card">
          <div className="card-body">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Page info */}
              <div className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </div>

              {/* Pagination buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </button>

                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          currentPage === pageNum
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Quote Modal */}
      <EditQuoteModal
        isOpen={showEditModal}
        onClose={handleEditClose}
        onSuccess={handleEditSuccess}
        quote={selectedQuote}
      />
    </div>
  );
};

export default QuoteTable;
